import React, { PropsWithChildren } from 'react';
import { Text, TextInput, View } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper, { WithColor } from '@styles/colors';
import CloseButton from '@components/closeButton';

import { scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { isWeb } from '@helpers';

import { IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageOPInformationTabsCrossIcon } from '@components/operatorPanel/hooks/useManageOPInformationTabsCrossIcon';

interface TopPanelProps extends PropsWithChildren, WithColor {
  title: string;
  closeButton?: boolean;
  editing?: boolean;
  setEditValue?: (v: string) => void;
  submit: () => void;
  withBottomBorder?: boolean;
  centerContent?: React.ReactNode;
}

const TopPanel: React.FC<TopPanelProps> = ({
  title,
  C,
  children,
  closeButton = true,
  editing = false,
  setEditValue,
  submit,
  withBottomBorder,
  centerContent,
}) => {
  const { onCrossIconPress } = useManageOPInformationTabsCrossIcon();

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/Panel [mobile]`, false);

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: scaleOP(IMAGE_HANDLING_HEADER_HEIGHT),
          paddingRight: scaleOP(22),
          paddingLeft: scaleOP(24),
          width: '100%',
          backgroundColor: bg,
          borderBottomWidth: withBottomBorder ? 0.5 : 0,
          borderBottomColor: withBottomBorder ? C('Hairline/Light/Transpar [mobile]') : 'transparent',
        },
      ]}
    >
      {editing ? (
        <TextInput
          style={
            (fontOP['Headline'],
            [
              {
                width: isWeb ? '100%' : '70%',
                height: scaleOP(22),
                backgroundColor: C('Background/Light/Field on Panel [mobile]'),
                color: C('Label/Dark/Primary'),
                borderRadius: scaleOP(4),
                paddingLeft: scaleOP(4),
                paddingRight: scaleOP(4),
                fontSize: scaleOP(16),
                marginRight: scaleOP(42),
                fontWeight: 600,
                outlineColor: '#97B3F6',
              },
            ])
          }
          value={title}
          onChangeText={setEditValue}
          onBlur={submit}
        />
      ) : (
        <Text
          style={[
            {
              color: C('Label/Light/Primary'),
            },
            fontOP['Headline'],
          ]}
        >
          {i18n.t(title)}
        </Text>
      )}

      {centerContent && (
        <View
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
          }}
        >
          {centerContent}
        </View>
      )}

      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: scaleOP(24),
          alignItems: 'center',
          marginLeft: 'auto',
        }}
      >
        {children}
        {closeButton ? (
          <CloseButton C={C} onPress={onCrossIconPress} scale={scaleOP} testID={TEST_IDS.operatorPanel.information.closeButton} />
        ) : null}
      </View>
    </View>
  );
};

export default ColorWrapper(TopPanel);
